import { HttpProgressEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { FetchStrategy } from '../instrumentation/instrumented-http.service';
import { Cacheable } from '../models/cacheable.model';
import { FileType } from '../models/file-item.model';
import { GraphDriveItem, GraphUploadSession, GraphUploadSessionStatus } from '../models/onedrive-graph.model';
import { OperationStream } from '../models/operation-stream.model';
import { MruFileItem } from '../mru/mru-file-item.model';
import { PromiseCancellationToken } from '../utilities/promise-cancellation-token.utility';
import { SchedulePriority } from './scheduler.service';

export interface OneDriveRootUrls extends Cacheable {
    tenantRoot: string;
    userRoot: string;
}

export type OneDriveAction = 'open' | 'editnew';

export interface GetDownloadUrlProps {
    driveId: string;
    itemId: string;
    itemUrl?: string;
}

@Injectable()
export abstract class OneDriveService {
    public abstract getMyDriveRootUrl(schedulePriority?: SchedulePriority): Promise<OneDriveRootUrls>;
    public abstract getMyDriveRootUrlStream(schedulePriority?: SchedulePriority): OperationStream<OneDriveRootUrls>;
    public abstract getSharedItemWithThumbnail(webUrl: string, documentType: FileType, fileFormat: string, strategy: FetchStrategy, fetchWacData: boolean, bypassMemoryCache?: boolean, schedulePriority?: SchedulePriority): Promise<GraphDriveItem>;
    public abstract getSharedItemWithThumbnailAndIds(webUrl: string, documentType: FileType, fileFormat: string, strategy: FetchStrategy, fetchWacData: boolean, bypassMemoryCache?: boolean, schedulePriority?: SchedulePriority): Promise<GraphDriveItem>;
    public abstract getSharedItemWithoutThumbnail(webUrl: string, documentType: FileType, fileFormat: string, strategy: FetchStrategy, fetchWacData: boolean, action?: OneDriveAction, bypassMemoryCache?: boolean, schedulePriority?: SchedulePriority): Promise<GraphDriveItem>;
    public abstract getItemWithWacDataAndThumbnails(item: MruFileItem, strategy: FetchStrategy, bypassMemoryCache?: boolean, schedulePriority?: SchedulePriority): Promise<GraphDriveItem>;
    public abstract getItemWithWacData(docUrl: string, driveId: string, docId: string, strategy: FetchStrategy, action?: string, bypassMemoryCache?: boolean, schedulePriority?: SchedulePriority): Promise<GraphDriveItem>;
    public abstract createNewFile(filename: string, fileType: FileType, fetchOpenWithData?: boolean): Promise<GraphDriveItem>;
    public abstract uploadFile(
        file: File,
        sessionId: string,
        parent?: string,
        path?: string,
        onProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number
    ): Promise<GraphDriveItem>;
    public abstract getDownloadUrl(getDownloadUrlProps: GetDownloadUrlProps): Promise<string>;
    public abstract createUploadSession(file: File, sessionId: string, parent?: string, path?: string, cancellationToken?: PromiseCancellationToken): Promise<GraphUploadSession>;
    public abstract cancelUploadSession(sessionUrl: string, sessionId: string, cancellationToken?: PromiseCancellationToken): Promise<void>;
    public abstract uploadFragment(
        sessionUrl: string,
        startByte: number,
        endByte: number,
        fileSize: number,
        content: Blob,
        sessionId: string,
        onProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeout?: number
    ): Promise<GraphUploadSessionStatus | GraphDriveItem>;
    public abstract getUploadSessionStatus(sessionUrl: string, sessionId: string, cancellationToken: PromiseCancellationToken): Promise<GraphDriveItem | GraphUploadSessionStatus>;
    public abstract getMyDriveRoot(strategy: FetchStrategy, schedulePriority?: SchedulePriority): Promise<GraphDriveItem>;
    public abstract getThumbnailString(url: string): Promise<string>;
}
